/**
 * PEBDEQ Frontend Constants
 * =========================
 * Centralized constants for the frontend application.
 */

// ===========================================
// ORDER STATUS
// ===========================================

export const ORDER_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  RETURN_REQUESTED: 'return_requested',
  RETURNED: 'returned',
};

export const ORDER_STATUS_LABELS = {
  [ORDER_STATUS.PENDING]: 'Pending',
  [ORDER_STATUS.PROCESSING]: 'Processing',
  [ORDER_STATUS.SHIPPED]: 'Shipped',
  [ORDER_STATUS.DELIVERED]: 'Delivered',
  [ORDER_STATUS.CANCELLED]: 'Cancelled',
  [ORDER_STATUS.RETURN_REQUESTED]: 'Return Requested',
  [ORDER_STATUS.RETURNED]: 'Returned',
};

export const ORDER_STATUS_COLORS = {
  [ORDER_STATUS.PENDING]: '#ffc107',
  [ORDER_STATUS.PROCESSING]: '#17a2b8',
  [ORDER_STATUS.SHIPPED]: '#007bff',
  [ORDER_STATUS.DELIVERED]: '#28a745',
  [ORDER_STATUS.CANCELLED]: '#dc3545',
  [ORDER_STATUS.RETURN_REQUESTED]: '#fd7e14',
  [ORDER_STATUS.RETURNED]: '#6c757d',
};

// ===========================================
// PAYMENT STATUS
// ===========================================

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
  REFUNDED: 'refunded',
};

export const PAYMENT_STATUS_LABELS = {
  [PAYMENT_STATUS.PENDING]: 'Pending',
  [PAYMENT_STATUS.PAID]: 'Paid',
  [PAYMENT_STATUS.FAILED]: 'Failed',
  [PAYMENT_STATUS.REFUNDED]: 'Refunded',
};

// ===========================================
// PAGINATION
// ===========================================

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 12,
  PAGE_SIZE_OPTIONS: [12, 24, 48, 96],
  MAX_PAGE_SIZE: 100,
};

// ===========================================
// FILE UPLOAD
// ===========================================

export const UPLOAD = {
  ALLOWED_IMAGE_TYPES: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'],
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/mov', 'video/avi', 'video/webm'],
  MAX_IMAGE_SIZE_MB: 10,
  MAX_VIDEO_SIZE_MB: 100,
};

// ===========================================
// ROUTES
// ===========================================

export const ROUTES = {
  HOME: '/',
  PRODUCTS: '/products',
  PRODUCT_DETAIL: '/product/:slug',
  CATEGORY: '/category/:slug',
  CART: '/cart',
  CHECKOUT: '/checkout',
  ORDER_CONFIRMATION: '/order-confirmation',
  
  // Auth
  LOGIN: '/login',
  REGISTER: '/register',
  
  // User
  PROFILE: '/profile',
  USER_DASHBOARD: '/user-dashboard',
  USER_SETTINGS: '/user-settings',
  ORDERS: '/orders',
  TRACK_ORDER: '/track-order',
  
  // Content
  BLOG: '/blog',
  BLOG_POST: '/blog/:slug',
  CONTACT: '/contact',
  ABOUT: '/about',
  FAQ: '/faq',
  
  // Admin
  ADMIN: '/admin',
  ADMIN_TESTS: '/admin/tests',
  
  // Legal
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_OF_SERVICE: '/terms-of-service',
  RETURN_POLICY: '/return-policy',
  COOKIE_POLICY: '/cookie-policy',
  SHIPPING_POLICY: '/shipping-policy',
};

// ===========================================
// API ENDPOINTS
// ===========================================

export const API = {
  // Auth
  LOGIN: '/api/auth/login',
  REGISTER: '/api/auth/register',
  PROFILE: '/api/auth/profile',
  LOGOUT: '/api/auth/logout',
  
  // Products
  PRODUCTS: '/api/products',
  PRODUCT_DETAIL: '/api/products/:slug',
  
  // Categories
  CATEGORIES: '/api/categories',
  
  // Orders
  ORDERS: '/api/orders',
  USER_ORDERS: '/api/user/orders',
  
  // Cart
  CART: '/api/cart',
  
  // Site Settings
  SITE_SETTINGS: '/api/site-settings',
};

// ===========================================
// LOCAL STORAGE KEYS
// ===========================================

export const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  CART: 'cart',
  THEME: 'theme',
  HOMEPAGE_STYLE: 'homepageStyle',
  CACHE_DEBUGGER: 'showCacheDebugger',
};

// ===========================================
// BRAND
// ===========================================

// Single source of truth for company name and copyright text.
// Phase 1 (2024): unify all fallbacks and landing copy to BRAND.COPYRIGHT_DEFAULT.
// To change globally later, edit COPYRIGHT_YEAR + the *_DEFAULT_* strings only.
export const BRAND = {
  NAME_LOWER: 'Pebdeq',
  NAME_UPPER: 'PEBDEQ',
  DOMAIN: 'https://www.pebdeq.com',
  COPYRIGHT_YEAR: 2026,
  COPYRIGHT_DEFAULT_EN: '© 2026 PEBDEQ. All rights reserved.',
  COPYRIGHT_DEFAULT_TR: '© 2026 PEBDEQ. Tüm hakları saklıdır.',
};

// ===========================================
// BREAKPOINTS
// ===========================================

export const BREAKPOINTS = {
  XS: 0,
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1400,
};

// ===========================================
// TOAST MESSAGES
// ===========================================

export const TOAST = {
  // Success
  LOGIN_SUCCESS: 'Welcome back!',
  REGISTER_SUCCESS: 'Account created successfully!',
  LOGOUT_SUCCESS: 'You have been logged out.',
  CART_ADDED: 'Item added to cart!',
  ORDER_PLACED: 'Order placed successfully!',
  
  // Error
  LOGIN_ERROR: 'Invalid email or password.',
  NETWORK_ERROR: 'Network error. Please try again.',
  SERVER_ERROR: 'Something went wrong. Please try again later.',
};
